import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react-native';
import { ProjectPaperContext } from '../components/papers/types';
import { ProjectPaperFeedback } from '../lib/projectPaperFeedback';
import { supabase, toError } from '../lib/supabase';
import {
    fetchCurrentEntitlements,
    invalidateEntitlementSnapshotCache,
} from '../lib/entitlements';
import {
    classifyBillingPreviewProjectAccess,
    getBillingPreviewState,
} from '../lib/billingPreviewState';
import {
    fetchPublicProjectPreviewAccess,
    PublicProjectPreviewNotFoundError,
    type PublicProjectPreviewAccess,
} from '../lib/publicDigestPreviewAccess';

interface ProjectRelation {
    name: string;
    user_id: string | null;
}

interface ProjectPaperContextRow {
    id: string;
    short_id: string | null;
    project_id: string;
    paper_id: string;
    summary: string | null;
    relevance_score: number | null;
    relevance_class: number | null;
    processing_state: string | null;
    matching_details: any;
    personalized_note: string | null;
    project: ProjectRelation | ProjectRelation[] | null;
    project_paper_feedback: ProjectPaperFeedback | ProjectPaperFeedback[] | null;
}

interface UseStandaloneProjectPaperContextOptions {
    paperId: string | null | undefined;
    projectId: string | null | undefined;
    projectPaperId: string | null | undefined;
    surface: string;
}

function getProjectName(project: ProjectRelation | ProjectRelation[] | null): string | null {
    if (Array.isArray(project)) {
        return project[0]?.name || null;
    }

    return project?.name || null;
}

function getFeedbackValue(
    feedback: ProjectPaperFeedback | ProjectPaperFeedback[] | null
): ProjectPaperFeedback | null {
    if (Array.isArray(feedback)) {
        return feedback[0] || null;
    }

    return feedback || null;
}

function mapRowToContext(row: ProjectPaperContextRow, projectName: string): ProjectPaperContext {
    const projectRelation = Array.isArray(row.project) ? row.project[0] : row.project;

    return {
        projectId: row.project_id,
        projectName,
        projectOwnerId: projectRelation?.user_id || null,
        projectPaperId: row.id,
        projectPaperShortId: row.short_id,
        feedback: getFeedbackValue(row.project_paper_feedback),
        recommendationSummary: row.summary,
        relevanceScore: row.relevance_score,
        relevanceClass: row.relevance_class,
        processingState: row.processing_state,
        matchingDetails: row.matching_details,
        personalizedNote: row.personalized_note,
    };
}

export function useStandaloneProjectPaperContext({
    paperId,
    projectId,
    projectPaperId,
    surface,
}: UseStandaloneProjectPaperContextOptions) {
    const [projectPaperContext, setProjectPaperContext] = useState<ProjectPaperContext | null>(null);
    const [isLoadingProjectPaperContext, setIsLoadingProjectPaperContext] = useState(false);

    useEffect(() => {
        let cancelled = false;

        async function fetchProjectPaperContext() {
            if (!paperId || !projectId || !projectPaperId) {
                setProjectPaperContext(null);
                setIsLoadingProjectPaperContext(false);
                return;
            }

            // Do not leave the previous route's project context visible while
            // this route is being classified. Every post-await write below is
            // guarded so a cancelled request cannot clear or replace a newer
            // route's context.
            setProjectPaperContext(null);
            setIsLoadingProjectPaperContext(true);

            try {
                const { data: { session }, error: sessionError } = await supabase.auth.getSession();
                if (cancelled) {
                    return;
                }
                if (sessionError) {
                    throw toError(sessionError);
                }

                let publicProjectAccess: PublicProjectPreviewAccess | null = null;
                try {
                    publicProjectAccess = await fetchPublicProjectPreviewAccess(projectId);
                } catch (publicAccessError) {
                    if (cancelled) {
                        return;
                    }
                    if (!(publicAccessError instanceof PublicProjectPreviewNotFoundError)) {
                        throw publicAccessError;
                    }
                }
                if (cancelled) {
                    return;
                }

                if (publicProjectAccess) {
                    if (
                        publicProjectAccess.preview_access === 'restricted'
                        && !publicProjectAccess.visible_project_paper_ids.includes(projectPaperId)
                    ) {
                        setProjectPaperContext(null);
                        setIsLoadingProjectPaperContext(false);
                        return;
                    }
                } else if (session?.access_token && session.user?.id) {
                    invalidateEntitlementSnapshotCache(session.user.id);
                    const snapshot = await fetchCurrentEntitlements(session.access_token, {
                        forceRefresh: true,
                        userId: session.user.id,
                    });
                    if (cancelled) {
                        return;
                    }
                    const previewAccess = classifyBillingPreviewProjectAccess(snapshot, projectId);
                    if (previewAccess !== 'restricted' && previewAccess !== 'unrestricted') {
                        setProjectPaperContext(null);
                        setIsLoadingProjectPaperContext(false);
                        return;
                    }
                    if (previewAccess === 'restricted') {
                        const resultDigestId = getBillingPreviewState(snapshot).resultDigestId;
                        if (!resultDigestId) {
                            setProjectPaperContext(null);
                            setIsLoadingProjectPaperContext(false);
                            return;
                        }

                        const { data: allowedPreviewPaper, error: previewPaperError } = await supabase
                            .from('digest_papers')
                            .select('project_paper_id')
                            .eq('digest_id', resultDigestId)
                            .eq('project_paper_id', projectPaperId)
                            .lte('rank_position', 3)
                            .maybeSingle();
                        if (cancelled) {
                            return;
                        }
                        if (previewPaperError) {
                            throw toError(previewPaperError);
                        }
                        if (!allowedPreviewPaper) {
                            setProjectPaperContext(null);
                            setIsLoadingProjectPaperContext(false);
                            return;
                        }
                    }
                } else {
                    setProjectPaperContext(null);
                    setIsLoadingProjectPaperContext(false);
                    return;
                }

                const { data, error } = await supabase
                    .from('project_paper')
                    .select(`
                        id,
                        short_id,
                        project_id,
                        paper_id,
                        summary,
                        relevance_score,
                        relevance_class,
                        processing_state,
                        matching_details,
                        personalized_note,
                        project:project_id (
                            name,
                            user_id
                        ),
                        project_paper_feedback (
                            vote,
                            downvote_reason,
                            updated_at
                        )
                    `)
                    .eq('id', projectPaperId)
                    .eq('project_id', projectId)
                    .single();

                if (cancelled) {
                    return;
                }

                if (error) {
                    const loggedError = toError(error);
                    console.warn('[useStandaloneProjectPaperContext] Failed to fetch project paper context:', loggedError);
                    setProjectPaperContext(null);
                    setIsLoadingProjectPaperContext(false);
                    return;
                }

                const row = data as unknown as ProjectPaperContextRow;
                if (row.paper_id !== paperId) {
                    console.warn('[useStandaloneProjectPaperContext] Project paper context mismatch:', {
                        paperId,
                        projectId,
                        projectPaperId,
                        fetchedPaperId: row.paper_id,
                    });
                    Sentry.captureMessage('Standalone project paper context mismatch', {
                        level: 'warning',
                        extra: {
                            paperId,
                            projectId,
                            projectPaperId,
                            fetchedPaperId: row.paper_id,
                            surface,
                        },
                    });
                    setProjectPaperContext(null);
                    setIsLoadingProjectPaperContext(false);
                    return;
                }

                const projectName = getProjectName(row.project);
                if (!projectName) {
                    console.warn('[useStandaloneProjectPaperContext] Missing project name for context:', {
                        paperId,
                        projectId,
                        projectPaperId,
                    });
                    setProjectPaperContext(null);
                    setIsLoadingProjectPaperContext(false);
                    return;
                }

                setProjectPaperContext(mapRowToContext(row, projectName));
            } catch (error: any) {
                if (!cancelled) {
                    console.warn('[useStandaloneProjectPaperContext] Unexpected context failure:', error);
                    Sentry.captureException(error, {
                        extra: { paperId, projectId, projectPaperId, surface },
                    });
                    setProjectPaperContext(null);
                }
            } finally {
                if (!cancelled) {
                    setIsLoadingProjectPaperContext(false);
                }
            }
        }

        void fetchProjectPaperContext();

        return () => {
            cancelled = true;
        };
    }, [paperId, projectId, projectPaperId, surface]);

    return {
        projectPaperContext,
        setProjectPaperContext,
        isLoadingProjectPaperContext,
    };
}
